import type { SignalMessage, IceCandidateEntry } from './TrainingSignalMessage';
import {
  dispatchConnectionStartAnalytics,
  dispatchConnectionCloseAnalytics,
  dispatchWebSocketOpenAnalytics,
  dispatchWebSocketErrorAnalytics,
  dispatchWebSocketCloseAnalytics,
  dispatchRetryAttemptAnalytics,
  dispatchRetryExhaustedAnalytics,
} from './trainingConnectionSavvyAnalytics';
import { buildTrainingIceServers } from './trainingIceServers';

import type { TrainingConnection } from 'features/training-forker/interfaces';
import type { SourceMediaTrack } from 'features/source-media-tracks/services';
import { SourceMediaTypes } from 'features/source-media-tracks/enums';
import { envConfig } from 'features/environment/services/';
import type { AppDispatch } from 'features/app/store/store';

// We use a small 16:9 frame (160x90) for the stub video tracks:
// - 16:9 keeps a typical aspect ratio so remote layout logic that inspects aspect does not get a 1:1 edge case.
// - 160x90 is far smaller than common camera resolutions (e.g. 1280x720) but still avoids very tiny (e.g. 1x1) encoder edge paths.
// - Solid black at 1 fps results in negligible bandwidth; dropping to smaller dimensions would not yield a meaningful reduction.
const STUB_VIDEO_WIDTH = 160;
const STUB_VIDEO_HEIGHT = 90;
const STUB_VIDEO_FPS = 1;

const MAX_RETRY_ATTEMPTS = 6;
const MAX_RETRIES_PER_ERROR_CODE = 3;
const INITIAL_RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 30_000;
const BACKOFF_MULTIPLIER = 2;
const GENERIC_RETRY_ERROR_BUCKET = 'error:generic';
const PEER_CONNECTION_RETRY_ERROR_BUCKET = 'peer-connection';

type ConnectionPhase = 'idle' | 'connecting' | 'connected' | 'closing';

let InstanceCount = 0;
let TotalPeerConnectionCount = 0;
let ActivePeerConnectionCount = 0;

function createSavvySessionId(callId: number) {
  return String(callId * 4);
}

// Represents a persistent entry in a map of local track senders, keyed by SourceMediaType. Each
// entry is persistent for the life of the connection
interface SenderEntry {
  transceiver: RTCRtpTransceiver;
  activeTrack: MediaStreamTrack; // currently attached (real or stub)
  stubTrack: MediaStreamTrack; // persistent stub used when real track missing
  isUsingStub: boolean;
  dispose?: () => void;
}

export function TrainingConnectionSavvyF(
  dispatch: AppDispatch,
  authToken?: string | null
): TrainingConnection {
  InstanceCount++;
  const SavvyWsUrl = envConfig?.trainingForker?.wsUrl?.trim() || '';

  const activeTracks = new Set<SourceMediaTrack>();
  const localSenderMap = new Map<SourceMediaTypes, SenderEntry>();

  let peerConnection: RTCPeerConnection | undefined;
  let socket: WebSocket | undefined;
  let savvySessionId = '';
  let callIdForSession: number | undefined;
  let retryAttempt = 0;
  const retryAttemptsByErrorCode = new Map<string, number>();
  let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;
  let closedIntentionally = false;
  let connectionPhase: ConnectionPhase = 'idle';
  const trainingIceServers = buildTrainingIceServers(envConfig?.sip);

  function resetRetryState() {
    retryAttempt = 0;
    retryAttemptsByErrorCode.clear();
    cancelPendingRetry();
  }

  const startConnection = async (
    tracks: SourceMediaTrack[],
    callId?: number
  ): Promise<void> => {
    if (connectionPhase === 'connecting' || connectionPhase === 'connected') {
      log(`startConnection skipped; phase=${connectionPhase}`);
      return;
    }

    connectionPhase = 'connecting';
    closedIntentionally = false;
    resetRetryState();

    log(
      `Starting connection (Instance ${InstanceCount}, Total connection #${TotalPeerConnectionCount}, Active connections: ${ActivePeerConnectionCount})`
    );
    setActiveTracks(tracks);

    log('Connecting to Savvy server...');
    log(`WebSocket URL: ${SavvyWsUrl}`);

    if (!SavvyWsUrl.length) {
      connectionPhase = 'idle';
      console.error(
        '[TrainingConnectionSavvy] No WebSocket URL defined in env.trainingForker.wsUrl. Connection will not be established.'
      );
      return;
    }

    if (typeof callId !== 'number' || !Number.isFinite(callId)) {
      connectionPhase = 'idle';
      log('startConnection skipped (missing call id)');
      return;
    }

    callIdForSession = callId;
    savvySessionId = createSavvySessionId(callId);
    log(
      `Using session mapping sourceCallId=${callIdForSession ?? 'not set'} -> sessionCallId=${savvySessionId}`
    );
    dispatchConnectionStartAnalytics(dispatch, {
      sessionId: savvySessionId,
      instanceNumber: InstanceCount,
      totalConnections: TotalPeerConnectionCount,
      activeConnections: ActivePeerConnectionCount,
    });
    connectWebSocket();
  };

  const closeConnection = () => {
    if (connectionPhase === 'idle') {
      return;
    }

    connectionPhase = 'closing';
    closedIntentionally = true;
    resetRetryState();

    if (peerConnection) {
      peerConnection.close();
      peerConnection = undefined;
      ActivePeerConnectionCount--;
    }

    if (socket) {
      const socketToClose = socket;
      socket = undefined;
      socketToClose.onopen = null;
      socketToClose.onmessage = null;
      socketToClose.onerror = null;

      if (
        socketToClose.readyState === WebSocket.CONNECTING ||
        socketToClose.readyState === WebSocket.OPEN
      ) {
        socketToClose.close();
      }
    }

    for (const entry of localSenderMap.values()) {
      entry?.dispose?.();
    }

    localSenderMap.clear();

    dispatchConnectionCloseAnalytics(dispatch, {
      instanceNumber: InstanceCount,
      totalConnections: TotalPeerConnectionCount,
      activeConnections: ActivePeerConnectionCount,
    });

    connectionPhase = 'idle';
    log('Connection closed');
  };

  const updateTracks = (sourceMediaTracks: SourceMediaTrack[]) => {
    if (!peerConnection) {
      return;
    }
    // Refresh our active track cache
    setActiveTracks(sourceMediaTracks);

    const newTracks = new Map<SourceMediaTypes, MediaStreamTrack>();
    for (const t of sourceMediaTracks) {
      newTracks.set(t.sourceMediaType, t.track);
    }

    for (const mediaType of localSenderMap.keys()) {
      replaceTrackForType(mediaType, newTracks.get(mediaType));
    }

    // After replacements, emit a status message so the backend (or debugging tools) can know
    // which media types are currently backed by real vs stub tracks.
    sendTracksUpdateMessage();
  };

  function log(message: string, ...args: any[]) {
    console.log(
      `[TrainingConnectionSavvy] savvySessionId: ${savvySessionId?.length > 0 ? savvySessionId : 'not set'} sessionCallId: ${savvySessionId?.length > 0 ? savvySessionId : 'not set'} sourceCallId: ${callIdForSession ?? 'not set'} ${message}`,
      ...args
    );
  }

  function createStubAudioTrack(): [MediaStreamTrack, () => void] {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    gain.gain.value = 0; // silence
    oscillator.connect(gain);

    const dest = ctx.createMediaStreamDestination();
    gain.connect(dest);

    oscillator.start();
    const track = dest.stream.getAudioTracks()[0];

    return [
      track,
      () => {
        try {
          oscillator.stop();
        } catch {}
        try {
          oscillator.disconnect();
          gain.disconnect();
        } catch {}
        ctx.close().catch(() => undefined);
      },
    ];
  }

  function createStubVideoTrack(): [MediaStreamTrack, () => void] {
    const canvas = document.createElement('canvas');
    canvas.width = STUB_VIDEO_WIDTH;
    canvas.height = STUB_VIDEO_HEIGHT;
    const ctx2d = canvas.getContext('2d');
    if (ctx2d) {
      ctx2d.fillStyle = 'black';
      ctx2d.fillRect(0, 0, canvas.width, canvas.height);
    }
    const interval = setInterval(() => {
      if (!ctx2d) {
        return;
      }
      ctx2d.fillStyle = 'black';
      ctx2d.fillRect(0, 0, canvas.width, canvas.height);
    }, 3000);
    const stream = canvas.captureStream(STUB_VIDEO_FPS);
    const track = stream.getVideoTracks()[0];
    return [
      track,
      () => {
        clearInterval(interval);
      },
    ];
  }

  function createStubTrackForType(
    type: SourceMediaTypes
  ): [MediaStreamTrack, () => void] {
    switch (type) {
      case SourceMediaTypes.HEARING_AUDIO:
      case SourceMediaTypes.INTERP_AUDIO:
      case SourceMediaTypes.DEAF_AUDIO:
        return createStubAudioTrack();
      case SourceMediaTypes.INTERP_VIDEO:
      case SourceMediaTypes.DEAF_VIDEO:
        return createStubVideoTrack();
      default:
        throw new Error(
          `Detected unknown SourceMediaType ${type} when creating stub track`
        );
    }
  }

  function setActiveTracks(tracks: SourceMediaTrack[]) {
    activeTracks.clear();

    for (const track of tracks) {
      activeTracks.add(track);
    }
  }

  function createIceCandidateEntry(
    candidate: RTCIceCandidate
  ): IceCandidateEntry {
    return {
      candidate: candidate.candidate ?? '',
      sdpMid: candidate.sdpMid ?? '',
      sdpMLineIndex: candidate.sdpMLineIndex ?? 0,
      usernameFragment: candidate.usernameFragment ?? '',
    };
  }

  function handleLocalIceCandidate(ev: RTCPeerConnectionIceEvent) {
    log('Local ICE candidate:', ev.candidate);

    if (ev.candidate) {
      sendSignalMessage({
        sessionId: savvySessionId,
        type: 'candidate',
        iceCandidates: [createIceCandidateEntry(ev.candidate)],
      });
    } else {
      log('All local ICE candidates have been handled.');
    }
  }

  function handleIceConnectionStateChange(_: Event) {
    log('ICE connection state change: ', peerConnection?.iceConnectionState);
    log('WARNING: ICE connection state change not implemented');
  }

  function handleConnectionStateChange(_: Event) {
    log('Connection state change: ', peerConnection?.connectionState);
    log('WARNING: Connection state change not implemented');
  }

  function handleRemoteTrack(_: RTCTrackEvent) {
    log('Remote track');
    log('WARNING: Remote track not implemented');
  }

  async function handleNewConnection() {
    if (!socket) {
      throw new Error('Socket not initialized');
    }

    TotalPeerConnectionCount++;
    ActivePeerConnectionCount++;

    peerConnection = new RTCPeerConnection({
      iceServers: trainingIceServers,
    });

    peerConnection.onicecandidate = (e) => handleLocalIceCandidate(e);
    peerConnection.oniceconnectionstatechange = (e) =>
      handleIceConnectionStateChange(e);
    peerConnection.onconnectionstatechange = (e) =>
      handleConnectionStateChange(e);
    peerConnection.ontrack = (e) => handleRemoteTrack(e);

    addInitialLocalSenders(peerConnection, activeTracks);
    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);

    sendSignalMessage({
      sessionId: savvySessionId,
      type: 'offer',
      sdp: offer.sdp ?? '',
      midMap: createMidMap(),
    });
  }

  function handleIncomingMessage(e: MessageEvent) {
    log('Received message:', e.data);

    const message = JSON.parse(e.data);
    handleSignalMessage(message);
  }

  async function handleSignalMessage(msg: SignalMessage) {
    switch (msg.type) {
      case 'answer':
        log('Received answer from Savvy server!!!');
        if (!peerConnection) {
          throw new Error('Peer connection not initialized for answer');
        }

        log('Setting remote description:', msg.sdp);
        await peerConnection.setRemoteDescription({
          type: 'answer',
          sdp: msg.sdp,
        });

        resetRetryState();

        break;

      case 'candidate':
        log('Received ICE candidate from Savvy server.');
        if (!peerConnection) {
          throw new Error('Peer connection not initialized for ICE candidate');
        }

        // Handle each ICE candidate in the array
        for (const iceCandidate of msg.iceCandidates) {
          await peerConnection.addIceCandidate(
            new RTCIceCandidate({
              candidate: iceCandidate.candidate,
              sdpMid: iceCandidate.sdpMid,
              sdpMLineIndex: iceCandidate.sdpMLineIndex,
              usernameFragment: iceCandidate.usernameFragment,
            })
          );
        }
        break;

      case 'health':
        // ignore
        break;

      default:
        console.warn('Unknown signal event:', msg);
        break;
    }
  }

  function handleSocketError(e: Event, socketForEvent?: WebSocket) {
    if (socketForEvent && socketForEvent !== socket) {
      return;
    }

    log('Socket error:', e);
    const activeSocket = socketForEvent ?? socket;
    if (activeSocket) {
      try {
        dispatchWebSocketErrorAnalytics(dispatch, {
          sessionId: savvySessionId,
          wsUrl: activeSocket.url,
          readyState: activeSocket.readyState,
          eventType: e.type,
          eventTimestamp: e.timeStamp,
        });
      } catch (err) {
        console.warn(
          '[TrainingConnectionSavvy] Analytics dispatch failed:',
          err
        );
      }
    }

    if (
      !closedIntentionally &&
      socketForEvent === socket &&
      retryTimeoutId === undefined
    ) {
      connectionPhase = 'connecting';
      scheduleRetry(GENERIC_RETRY_ERROR_BUCKET);
    }
  }

  function handleSocketClose(e: CloseEvent, socketForEvent?: WebSocket) {
    if (socketForEvent && socket !== undefined && socketForEvent !== socket) {
      return;
    }
    const activeSocket = socketForEvent ?? socket;
    try {
      dispatchWebSocketCloseAnalytics(dispatch, {
        sessionId: savvySessionId,
        wsUrl: activeSocket?.url,
        code: e.code,
        reason: e.reason,
        wasClean: e.wasClean,
        eventType: e.type,
        eventTimestamp: e.timeStamp,
        readyState: activeSocket?.readyState,
      });
      log('Socket closed:', e);
    } catch (err) {
      console.warn('[TrainingConnectionSavvy] Analytics dispatch failed:', err);
    }

    if (closedIntentionally) {
      connectionPhase = 'idle';
      return;
    }

    if (!closedIntentionally && socketForEvent === socket) {
      connectionPhase = 'connecting';
      scheduleRetry(`close:${e.code}`);
    }
  }

  function connectWebSocket() {
    const token = authToken ?? 'MOCKAUTH';
    try {
      const createdSocket = new WebSocket(SavvyWsUrl, [
        'authorization-jwt',
        token,
      ]);
      socket = createdSocket;
      createdSocket.onopen = (e) => {
        if (socket !== createdSocket) {
          return;
        }

        log('WebSocket opened:', e);

        connectionPhase = 'connected';
        retryAttempt = 0;
        try {
          dispatchWebSocketOpenAnalytics(dispatch, {
            sessionId: savvySessionId,
            wsUrl: SavvyWsUrl,
            eventType: e.type,
            eventTimestamp: e.timeStamp,
          });
        } catch (err) {
          console.warn(
            '[TrainingConnectionSavvy] Analytics dispatch failed:',
            err
          );
        }
        handleNewConnection().catch((err) => {
          log('Error establishing peer connection:', err);

          if (!closedIntentionally && socket === createdSocket) {
            connectionPhase = 'connecting';
            scheduleRetry(PEER_CONNECTION_RETRY_ERROR_BUCKET);
          }
        });
      };
      createdSocket.onmessage = (e) => handleIncomingMessage(e);
      createdSocket.onerror = (e) => handleSocketError(e, createdSocket);
      createdSocket.onclose = (e) => handleSocketClose(e, createdSocket);
    } catch (e) {
      log('Error connecting to Savvy server:', e);
      if (!closedIntentionally) {
        scheduleRetry(GENERIC_RETRY_ERROR_BUCKET);
      }
    }
  }

  function cancelPendingRetry() {
    if (retryTimeoutId !== undefined) {
      globalThis.clearTimeout(retryTimeoutId);
      retryTimeoutId = undefined;
    }
  }

  function cleanupBeforeRetry() {
    if (peerConnection) {
      peerConnection.close();
      peerConnection = undefined;
      ActivePeerConnectionCount--;
    }

    if (socket) {
      const socketToClose = socket;
      socket = undefined;
      socketToClose.onopen = null;
      socketToClose.onmessage = null;
      socketToClose.onerror = null;
      socketToClose.onclose = null;

      if (
        socketToClose.readyState === WebSocket.CONNECTING ||
        socketToClose.readyState === WebSocket.OPEN
      ) {
        socketToClose.close();
      }
    }

    for (const entry of localSenderMap.values()) {
      entry?.dispose?.();
    }
    localSenderMap.clear();
  }

  function scheduleRetry(errorBucket: string) {
    if (retryTimeoutId !== undefined) {
      return;
    }

    const errorCodeRetryAttempts =
      retryAttemptsByErrorCode.get(errorBucket) ?? 0;

    if (errorCodeRetryAttempts >= MAX_RETRIES_PER_ERROR_CODE) {
      log(
        `Retry limit reached for ${errorBucket} (${MAX_RETRIES_PER_ERROR_CODE}). Giving up — entering degraded state.`
      );
      connectionPhase = 'idle';
      dispatchRetryExhaustedAnalytics(dispatch, {
        sessionId: savvySessionId,
        totalAttempts: retryAttempt,
        wsUrl: SavvyWsUrl,
      });
      return;
    }

    if (retryAttempt >= MAX_RETRY_ATTEMPTS) {
      log(
        `Retry limit reached (${MAX_RETRY_ATTEMPTS}). Giving up — entering degraded state.`
      );
      connectionPhase = 'idle';
      dispatchRetryExhaustedAnalytics(dispatch, {
        sessionId: savvySessionId,
        totalAttempts: retryAttempt,
        wsUrl: SavvyWsUrl,
      });
      return;
    }

    const delayMs = Math.min(
      INITIAL_RETRY_DELAY_MS * Math.pow(BACKOFF_MULTIPLIER, retryAttempt),
      MAX_RETRY_DELAY_MS
    );
    retryAttempt++;
    retryAttemptsByErrorCode.set(errorBucket, errorCodeRetryAttempts + 1);

    log(
      `Scheduling WebSocket retry ${retryAttempt}/${MAX_RETRY_ATTEMPTS} in ${delayMs}ms`
    );
    dispatchRetryAttemptAnalytics(dispatch, {
      sessionId: savvySessionId,
      attempt: retryAttempt,
      maxAttempts: MAX_RETRY_ATTEMPTS,
      delayMs,
      wsUrl: SavvyWsUrl,
    });

    retryTimeoutId = globalThis.setTimeout(() => {
      retryTimeoutId = undefined;
      cleanupBeforeRetry();
      connectWebSocket();
    }, delayMs);
  }

  function addInitialLocalSenders(
    peer: RTCPeerConnection,
    providedTracks: Set<SourceMediaTrack>
  ) {
    const providedMap = new Map<SourceMediaTypes, MediaStreamTrack>(
      Array.from(providedTracks).map((t) => [t.sourceMediaType, t.track])
    );

    // We provision a transceiver (with a sender carrying either a real or stub track) for EVERY SourceMediaType up front.
    for (const mediaType of Object.values(SourceMediaTypes)) {
      const realTrack = providedMap.get(mediaType);
      const [stubTrack, disposeStubTrack] = createStubTrackForType(mediaType);
      const activeTrack = realTrack ?? stubTrack;
      const transceiver = peer.addTransceiver(activeTrack, {
        direction: 'sendonly',
      });

      localSenderMap.set(mediaType, {
        transceiver,
        activeTrack: activeTrack,
        stubTrack,
        isUsingStub: !realTrack,
        dispose: disposeStubTrack,
      });

      log(
        `Added initial sender for ${mediaType} (${realTrack ? 'real' : 'stub'})`
      );
    }
  }

  function replaceTrackForType(
    mediaType: SourceMediaTypes,
    newTrack: MediaStreamTrack | undefined
  ) {
    const entry = localSenderMap.get(mediaType);
    if (!entry) {
      log(`replaceTrackForType: no sender entry for ${mediaType}`);
      return;
    }
    const newActiveTrack = newTrack ?? entry.stubTrack;
    if (entry.activeTrack === newActiveTrack) {
      return;
    }

    log(
      `Replacing track for ${mediaType} with ${newTrack ? 'real' : 'stub'} track`
    );
    entry.transceiver.sender
      .replaceTrack(newActiveTrack)
      .catch((err) => log('replaceTrack error', err));
    entry.activeTrack = newActiveTrack;
    entry.isUsingStub = !newTrack;
  }

  function sendTracksUpdateMessage() {
    log('##### Preparing to send tracks status message #####');
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }

    const tracksUpdateMsg: SignalMessage = {
      sessionId: savvySessionId,
      type: 'tracks_update',
      midMap: createMidMap(),
    };
    sendSignalMessage(tracksUpdateMsg);
    log('Sent tracks-update message', tracksUpdateMsg);
  }

  function createMidMap(): Record<string, string> {
    const midMap: Record<string, string> = {};
    localSenderMap.forEach((entry, mediaType) => {
      const mid = entry?.transceiver?.mid;
      if (typeof mid !== 'string' || mid.length === 0) {
        log(
          `WARNING: Skipping midMap entry for ${mediaType} because transceiver.mid is not a valid string (value: ${mid})`
        );
        return;
      }
      midMap[mid] = mediaType;
    });

    return midMap;
  }

  function sendSignalMessage(message: SignalMessage) {
    if (!socket) {
      log('sendSignalMessage skipped (no socket)', message);
      return;
    }
    if (socket.readyState !== WebSocket.OPEN) {
      log(
        'sendSignalMessage skipped (socket not OPEN)',
        message,
        'state:',
        socket.readyState
      );
      return;
    }
    try {
      log('Sending signal message:', JSON.stringify(message));
      socket.send(JSON.stringify(message));
    } catch (e) {
      log('sendSignalMessage failed', e);
    }
  }

  const connection: TrainingConnection = {
    startConnection: startConnection,
    closeConnection: closeConnection,
    updateTracks: updateTracks,
  };

  return connection;
}
